import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { Loader2, Building2, Users, GitBranch, Plus, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useUser } from '@/hooks/use-user';
import { hasCommunityPermission } from '@/lib/community-permissions';

interface CommunityNode {
  id: string;
  name: string;
  slug: string | null;
  logo: string | null;
  parentCommunityId: string | null;
  depth: number;
  memberCount?: number;
}

export default function SubCommunityPage() {
  const navigate = useNavigate();
  const { communityId } = useParams<{ communityId: string }>();
  const { communities } = useUser();
  const canCreateSubcommunity = hasCommunityPermission(
    communities.find((community) => community.id === communityId)?.permissions,
    'create_subcommunity'
  );
  const [children, setChildren] = useState<CommunityNode[]>([]);
  const [tree, setTree] = useState<CommunityNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTreeLoading, setIsTreeLoading] = useState(false);
  const [showTreeDialog, setShowTreeDialog] = useState(false);

  useEffect(() => {
    if (!communityId) return;
    fetchChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  const fetchChildren = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/api/communities/${communityId}/children`);
      setChildren(response.data.children || []);
    } catch (error) {
      console.error('Error fetching children:', error);
      toast.error('Failed to load sub-communities');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTree = async () => {
    try {
      setIsTreeLoading(true);

      const ancestorsRes = await axiosInstance.get(`/api/communities/${communityId}/ancestors`);
      const ancestors = ancestorsRes.data.ancestors || [];

      const fullTree: CommunityNode[] = [];
      const seenIds = new Set<string>();

      for (const ancestor of ancestors) {
        const treeRes = await axiosInstance.get(`/api/communities/${ancestor.id}/tree`);
        const ancestorTree = treeRes.data.tree || [];
        for (const node of ancestorTree) {
          if (!seenIds.has(node.id)) {
            seenIds.add(node.id);
            fullTree.push(node);
          }
        }
      }

      const currentTreeRes = await axiosInstance.get(`/api/communities/${communityId}/tree`);
      const currentTree = currentTreeRes.data.tree || [];
      for (const node of currentTree) {
        if (!seenIds.has(node.id)) {
          seenIds.add(node.id);
          fullTree.push(node);
        }
      }

      setTree(fullTree);
    } catch (error) {
      console.error('Error fetching tree:', error);
      toast.error('Failed to load community tree');
    } finally {
      setIsTreeLoading(false);
    }
  };

  const handleViewTree = async () => {
    setShowTreeDialog(true);
    if (tree.length === 0) {
      await fetchTree();
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Sub Communities</h1>
          <p className="text-muted-foreground">Manage sub-communities under your community</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={handleViewTree}>
            <GitBranch className="mr-2 h-4 w-4" />
            View Tree
          </Button>
          {canCreateSubcommunity && (
            <Button onClick={() => navigate(`/create-community?tab=create&parent=${communityId}`)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Sub Community
            </Button>
          )}
        </div>
      </div>

      {children.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Sub Communities Yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first sub-community to organize members into smaller groups.
            </p>
            {canCreateSubcommunity && (
              <Button
                onClick={() => navigate(`/create-community?tab=create&parent=${communityId}`)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Sub Community
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {children.map((child) => (
            <Card
              key={child.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/${child.id}/dashboard`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center overflow-hidden">
                    {child.logo ? (
                      <img
                        src={child.logo}
                        alt={child.name}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <Building2 className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">{child.name}</CardTitle>
                    <CardDescription className="text-xs">@{child.slug}</CardDescription>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>{child.memberCount || 0} members</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showTreeDialog} onOpenChange={setShowTreeDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Community Tree</DialogTitle>
          </DialogHeader>
          {isTreeLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-2">
              {tree.map((node) => (
                <div
                  key={node.id}
                  className="flex items-center gap-2"
                  style={{ paddingLeft: `${node.depth * 24}px` }}
                >
                  <div
                    className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer flex-1"
                    onClick={() => {
                      navigate(`/${node.id}/dashboard`);
                      setShowTreeDialog(false);
                    }}
                  >
                    <Building2 className="h-4 w-4 text-primary" />
                    <span className="font-medium">{node.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({node.memberCount || 0} members)
                    </span>
                  </div>
                </div>
              ))}
              {tree.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No communities in tree</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
